import React, { useEffect, useRef, useState } from 'react';
import { THEME_MODES } from '../constants';
import logger from '../logger';
import { PortalMessages as M } from '../messages';
import { userMediaUrl } from '../profileMedia';
import { getThemeMode, saveThemeMode } from '../settings';
import AccountSecurityPanel from './AccountSecurityPanel';
import AiAssistantPage from './AiAssistantPage';
import ProfileEditPage from './ProfileEditPage';
/** Toast 展示时长（毫秒）。 */
const TODO_TOAST_MS = 1800;
/**
 * 线型图标集合（24×24 viewBox，stroke=currentColor，无填充）。
 * 与参考图风格保持一致；颜色由父级 color 控制，主题切换零成本。
 *
 * @param {Object} props - 图标属性。
 * @param {string} props.d - SVG path。
 * @returns {JSX.Element}
 */
const Icon = ({ d }) => (React.createElement("svg", { "aria-hidden": true, fill: 'none', focusable: 'false', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 1.75, viewBox: '0 0 24 24' },
    React.createElement("path", { d: d })));
/** 6 个快捷入口（按参考图顺序）。图标统一为 24×24 线框；「录制」跳转录制页，「AI」进问答，其余 toast。 */
const SHORTCUTS = [
    {
        iconPath: 'M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z M9 22V12h6v10',
        id: 'personalRoom',
        label: M.PROFILE_SHORTCUT_PERSONAL_ROOM
    },
    {
        iconPath: 'M23 7l-7 5 7 5V7z M14 5H3a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2z',
        id: 'recordings',
        label: M.PROFILE_SHORTCUT_RECORDINGS
    },
    {
        iconPath: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8',
        id: 'notes',
        label: M.PROFILE_SHORTCUT_NOTES
    },
    {
        iconPath: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z',
        id: 'ai',
        label: M.PROFILE_SHORTCUT_AI
    },
    {
        iconPath: 'M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2 M15 2H9a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1z M8 11h8 M8 15h6',
        id: 'orders',
        label: M.PROFILE_SHORTCUT_ORDERS
    },
    {
        iconPath: 'M3 3h7v7H3z M14 3h7v7h-7z M14 14h7v7h-7z M3 14h7v7H3z',
        id: 'controlRooms',
        label: M.PROFILE_SHORTCUT_CONTROL_ROOMS
    }
];
/** 顶部工具栏 3 个图标：太阳 / 加人 / 扫一扫。 */
const TOPBAR_ICONS = [
    {
        ariaLabel: M.PROFILE_TOPBAR_THEME,
        iconPath: 'M12 4v2m0 12v2M4 12h2m12 0h2M5.6 5.6l1.4 1.4m10 10l1.4 1.4M5.6 18.4l1.4-1.4m10-10l1.4 1.4',
        id: 'brightness'
    },
    {
        ariaLabel: M.PROFILE_TOPBAR_ADD_FRIEND,
        iconPath: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 7a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM19 8v6M22 11h-6',
        id: 'addFriend'
    },
    {
        ariaLabel: M.PROFILE_TOPBAR_SCAN,
        iconPath: 'M4 8V5a1 1 0 0 1 1-1h3M16 4h3a1 1 0 0 1 1 1v3M4 16v3a1 1 0 0 0 1 1h3M16 20h3a1 1 0 0 0 1-1v-3M4 12h16',
        id: 'scan'
    }
];
/**
 * 菜单列表。
 * - 「积分中心」暂注释（何总要求先去掉，不删）；
 * - 「设置」走 onOpenSettings 打开原生设置弹窗（保留功能，样式由 _portal.scss 统一）；
 * - 「账号与安全」跳转到 AccountSecurityPanel（对接后端）；
 * - 「隐私 / 帮助与客服 / 关于我们」点击后跳转到详情视图（文案写死，TODO 待对接）；
 * - 「关于我们」红点暂去掉（何总要求）。
 */
const MENU = [
    // { id: 'points', label: M.PROFILE_MENU_POINTS, variant: 'pointsExtra' },  // 暂去掉积分中心（何总要求，不删）
    { id: 'security', label: M.PROFILE_MENU_SECURITY },
    { id: 'settings', label: M.PROFILE_MENU_SETTINGS },
    { id: 'privacy', label: M.PROFILE_MENU_PRIVACY },
    { id: 'help', label: M.PROFILE_MENU_HELP },
    { id: 'about', label: M.PROFILE_MENU_ABOUT }
];
/**
 * 隐私 / 帮助 / 关于 可跳转详情的占位文案（写死，TODO 待对接）。
 * 多行文案用 \n 分隔，渲染时 split 成 <p>。
 */
const MENU_CONTENT = {
    privacy: M.PROFILE_PRIVACY_CONTENT,
    help: M.PROFILE_HELP_CONTENT,
    about: M.PROFILE_ABOUT_CONTENT
};
/**
 * 解析当前实际深/浅色：system 时读 html[data-theme]。
 *
 * @param {ThemeMode} mode - 当前主题偏好。
 * @returns {string} 实际生效的 dark 或 light。
 */
function resolveEffectiveTheme(mode) {
    if (mode === THEME_MODES.DARK || mode === THEME_MODES.LIGHT) {
        return mode;
    }
    const attr = document.documentElement.getAttribute('data-theme');
    return attr === THEME_MODES.DARK ? THEME_MODES.DARK : THEME_MODES.LIGHT;
}
/**
 * 手机端「我的」资料页。
 *
 * 两种视图：
 * 1. 菜单列表视图（默认）：顶部工具栏 + 头像区 + 额度卡 + 试用 banner + 宫格 + 菜单 + 退出登录
 * 2. 详情视图（跳转）：返回按钮 + 标题 + 内容段落。
 *
 * @param {IProps} props - 组件属性。
 * @returns {JSX.Element}
 */
export default function ProfilePage({ onLogout, onOpenRecordings, onOpenSettings, onThemeChange, onUserUpdated, openInnerAsModal = false, user }) {
    const [themeMode, setThemeMode] = useState(getThemeMode());
    const [activeMenuId, setActiveMenuId] = useState(null);
    const [toastVisible, setToastVisible] = useState(false);
    const toastTimerRef = useRef(null);
    const displayName = user.displayName || user.phone || '用户';
    const avatarText = displayName.slice(0, 1).toUpperCase();
    const avatarSrc = userMediaUrl('avatar', user);
    const signatureText = user.signature?.trim() || M.PROFILE_SIGNATURE_PLACEHOLDER;
    useEffect(() => () => {
        if (toastTimerRef.current !== null) {
            window.clearTimeout(toastTimerRef.current);
        }
    }, []);
    useEffect(() => {
        if (!openInnerAsModal || !activeMenuId) {
            return;
        }
        const onKeyDown = (event) => {
            if (event.key !== 'Escape') {
                return;
            }
            event.stopImmediatePropagation();
            logger.info(`[我的] ${new Date().toISOString()} Esc 关闭内层弹窗 | from=${activeMenuId}`);
            setActiveMenuId(null);
        };
        window.addEventListener('keydown', onKeyDown, true);
        return () => window.removeEventListener('keydown', onKeyDown, true);
    }, [openInnerAsModal, activeMenuId]);
    /**
     * 切换门户主题并持久化。
     *
     * @param {ThemeMode} mode - 目标主题。
     * @returns {void}
     */
    const onSelectTheme = (mode) => {
        saveThemeMode(mode);
        setThemeMode(mode);
        onThemeChange(mode);
        logger.info(`[我的] ${new Date().toISOString()} 切换主题 | themeMode=${mode}`);
    };
    /**
     * 退出登录。
     *
     * @returns {void}
     */
    const onLogoutClick = () => {
        logger.info(`[我的] ${new Date().toISOString()} 用户点击退出登录`);
        onLogout();
    };
    /** 未对接功能：toast + 中文日志。
     *
     * @param {string} source - 点击来源标识。
     * @param {boolean} placeholder - 是否为静态占位入口。
     * @returns {void}
     */
    const showTodoToast = (source, placeholder = false) => {
        logger.info(`[我的] ${new Date().toISOString()} 功能暂未开发 | source=${source}`
            + (placeholder ? ' placeholder=true' : ''));
        setToastVisible(true);
        if (toastTimerRef.current !== null) {
            window.clearTimeout(toastTimerRef.current);
        }
        toastTimerRef.current = window.setTimeout(() => {
            setToastVisible(false);
            toastTimerRef.current = null;
        }, TODO_TOAST_MS);
    };
    /** 6 个快捷入口：「录制」跳转，「AI」进问答，其余 toast。
     *
     * @param {string} id - 入口 id。
     * @param {string} label - 入口文案。
     * @returns {void}
     */
    const onShortcutClick = (id, label) => {
        logger.info(`[我的] ${new Date().toISOString()} 点击快捷入口 | id=${id} label=${label}`);
        if (id === 'recordings') {
            logger.info(`[我的] ${new Date().toISOString()} 打开录制页`);
            onOpenRecordings();
            return;
        }
        if (id === 'ai') {
            logger.info(`[我的] ${new Date().toISOString()} 打开 AI 小助手`);
            setActiveMenuId('ai');
            return;
        }
        showTodoToast(`shortcut.${id}`);
    };
    /**
     * 菜单点击：
     * - 「设置」走 onOpenSettings 打开原生设置弹窗（保留功能）；
     * - 「账号与安全」进入对接后端的安全面板；
     * - 有详情内容的项（privacy/help/about）跳转到详情视图；
     * - 其它打日志 + TODO。
     *
     * @param {string} id - 菜单项 id。
     * @returns {void}
     */
    const onMenuClick = (id) => {
        logger.info(`[我的] ${new Date().toISOString()} 点击菜单项 | id=${id}`);
        if (id === 'settings') {
            logger.info(`[我的] ${new Date().toISOString()} 打开原生设置弹窗`);
            onOpenSettings();
            return;
        }
        if (id === 'security' || MENU_CONTENT[id]) {
            setActiveMenuId(id);
            return;
        }
        // TODO(portal/profile): 为 points 补跳转路由
    };
    /** 顶部工具栏：太阳切换主题，加人/扫一扫 toast。
     *
     * @param {string} id - 工具栏按钮 id。
     * @returns {void}
     */
    const onTopbarClick = (id) => {
        logger.info(`[我的] ${new Date().toISOString()} 点击顶部工具栏 | id=${id}`);
        if (id === 'brightness') {
            const next = resolveEffectiveTheme(themeMode) === THEME_MODES.DARK
                ? THEME_MODES.LIGHT
                : THEME_MODES.DARK;
            onSelectTheme(next);
            return;
        }
        showTodoToast(`topbar.${id}`);
    };
    /** 打开「我的资料」编辑页。
     *
     * @returns {void}
     */
    const openMyInfo = () => {
        logger.info(`[我的] ${new Date().toISOString()} 打开我的资料`);
        setActiveMenuId('myInfo');
    };
    /**
     * 从详情视图返回菜单列表。
     *
     * @returns {void}
     */
    const onDetailBack = () => {
        logger.info(`[我的] ${new Date().toISOString()} 从详情视图返回 | from=${activeMenuId}`);
        setActiveMenuId(null);
    };
    /** 当前详情视图的标题 */
    const activeMenuLabel = activeMenuId
        ? MENU.find(m => m.id === activeMenuId)?.label ?? ''
        : '';
    let innerView = null;
    if (activeMenuId === 'security') {
        innerView = (React.createElement(AccountSecurityPanel, { onBack: onDetailBack, onLogout: onLogout, user: user }));
    }
    else if (activeMenuId === 'myInfo') {
        innerView = (React.createElement(ProfileEditPage, { onBack: onDetailBack, onUserUpdated: onUserUpdated, user: user }));
    }
    else if (activeMenuId === 'ai') {
        innerView = (React.createElement(AiAssistantPage, { onBack: onDetailBack }));
    }
    else if (activeMenuId && MENU_CONTENT[activeMenuId]) {
        innerView = (React.createElement("div", { className: 'portal-profile portal-profile-detail-view' },
            React.createElement("header", { className: 'portal-profile-detail-header' },
                React.createElement("button", { "aria-label": M.HISTORY_BACK, className: 'portal-profile-detail-back', onClick: onDetailBack, type: 'button' }, '‹'),
                React.createElement("span", { className: 'portal-profile-detail-title' }, activeMenuLabel)),
            React.createElement("div", { className: 'portal-profile-detail-body' }, MENU_CONTENT[activeMenuId].split('\n').map((line, i) => (React.createElement("p", { key: i }, line))))));
    }
    if (innerView && !openInnerAsModal) {
        return innerView;
    }
    const hub = (React.createElement("div", { className: 'portal-profile' },
        React.createElement("section", { "aria-label": 'tools', className: 'portal-profile-topbar' }, TOPBAR_ICONS.map(t => (React.createElement("button", { "aria-label": t.ariaLabel, className: 'portal-profile-topbar-btn', key: t.id, onClick: () => onTopbarClick(t.id), type: 'button' },
            React.createElement(Icon, { d: t.iconPath }))))),
        React.createElement("section", { "aria-label": M.PROFILE_TITLE, className: 'portal-profile-header' },
            React.createElement("button", { "aria-label": M.PROFILE_MY_INFO, className: 'portal-profile-header-avatar', onClick: openMyInfo, type: 'button' },
                avatarSrc ? (React.createElement("img", { alt: displayName, onError: event => {
                        event.currentTarget.style.display = 'none';
                    }, src: avatarSrc })) : null,
                React.createElement("span", null, avatarText)),
            React.createElement("div", { className: 'portal-profile-header-main' },
                React.createElement("div", { className: 'portal-profile-header-row' },
                    React.createElement("h2", { className: 'portal-profile-header-name' }, displayName),
                    React.createElement("button", { "aria-label": M.PROFILE_MY_INFO, className: 'portal-profile-header-link', onClick: openMyInfo, type: 'button' },
                        M.PROFILE_MY_INFO,
                        React.createElement("span", { "aria-hidden": true }, ' ›'))),
                React.createElement("span", { className: 'portal-profile-header-badge' }, M.PROFILE_PLAN_FREE),
                React.createElement("button", { "aria-label": signatureText, className: 'portal-profile-header-signature', onClick: openMyInfo, type: 'button' }, signatureText))),
        React.createElement("section", { "aria-label": M.PROFILE_QUOTA_TITLE, className: 'portal-profile-quota' },
            React.createElement("h3", { className: 'portal-profile-quota-title' }, M.PROFILE_QUOTA_TITLE),
            React.createElement("button", { "aria-label": `${M.PROFILE_QUOTA_TWO_UNLIMITED} ${M.PROFILE_QUOTA_EXPAND}`, className: 'portal-profile-quota-row', onClick: () => showTodoToast('quota.expand', true), type: 'button' },
                React.createElement("span", null, M.PROFILE_QUOTA_TWO_UNLIMITED),
                React.createElement("span", { className: 'portal-profile-quota-action' },
                    M.PROFILE_QUOTA_EXPAND,
                    ' ›')),
            React.createElement("button", { "aria-label": M.PROFILE_QUOTA_LARGE_LIMITED, className: 'portal-profile-quota-row', onClick: () => showTodoToast('quota.largeLimited', true), type: 'button' },
                React.createElement("span", null, M.PROFILE_QUOTA_LARGE_LIMITED),
                React.createElement("span", { "aria-hidden": true, className: 'portal-profile-quota-info' }, 'i')),
            React.createElement("button", { "aria-label": M.PROFILE_QUOTA_EXTRA_CARDS, className: 'portal-profile-quota-row', onClick: () => showTodoToast('quota.extraCards', true), type: 'button' },
                React.createElement("span", null, M.PROFILE_QUOTA_EXTRA_CARDS),
                React.createElement("span", { "aria-hidden": true, className: 'portal-profile-quota-chevron' }, '›')),
            React.createElement("div", { className: 'portal-profile-quota-cta' },
                React.createElement("span", { className: 'portal-profile-quota-hint' }, M.PROFILE_QUOTA_UPGRADE_HINT),
                React.createElement("button", { className: 'portal-profile-quota-upgrade', onClick: () => showTodoToast('quota.upgrade', true), type: 'button' }, M.PROFILE_QUOTA_UPGRADE))),
        React.createElement("button", { "aria-label": M.PROFILE_TRIAL_TITLE, className: 'portal-profile-trial', onClick: () => showTodoToast('trial.banner'), type: 'button' },
            React.createElement("span", { className: 'portal-profile-trial-text' },
                React.createElement("span", { className: 'portal-profile-trial-title' }, M.PROFILE_TRIAL_TITLE),
                React.createElement("span", { className: 'portal-profile-trial-sub' }, M.PROFILE_BANNER)),
            React.createElement("span", { "aria-hidden": true, className: 'portal-profile-trial-chevron' }, '›')),
        React.createElement("section", { "aria-label": 'shortcuts', className: 'portal-profile-shortcuts' }, SHORTCUTS.map(s => (React.createElement("button", { "aria-label": s.label, className: 'portal-profile-shortcut', key: s.id, onClick: () => onShortcutClick(s.id, s.label), type: 'button' },
            React.createElement("span", { "aria-hidden": true, className: 'portal-profile-shortcut-icon' },
                React.createElement(Icon, { d: s.iconPath })),
            React.createElement("span", { className: 'portal-profile-shortcut-label' }, s.label))))),
        React.createElement("section", { "aria-label": 'menu', className: 'portal-profile-menu' }, MENU.map(item => (React.createElement("button", { "aria-label": item.label, className: 'portal-profile-menu-item', key: item.id, onClick: () => onMenuClick(item.id), type: 'button' },
            React.createElement("span", { className: 'portal-profile-menu-label' }, item.label),
            item.variant === 'pointsExtra' ? (React.createElement("span", { "aria-hidden": true, className: 'portal-profile-menu-extra' }, M.PROFILE_MENU_POINTS_EXTRA)) : item.variant === 'aboutDot' ? (React.createElement("span", { "aria-label": M.PROFILE_MENU_ABOUT_DOT_LABEL, className: 'portal-profile-menu-dot', role: 'img' })) : (React.createElement("span", { "aria-hidden": true, className: 'portal-profile-menu-chevron' }, '›')))))),
        React.createElement("section", { className: 'portal-profile-logout-wrap' },
            React.createElement("button", { "aria-label": M.LOGOUT, className: 'portal-profile-logout', onClick: onLogoutClick, type: 'button' }, M.LOGOUT)),
        toastVisible && (React.createElement("div", { className: 'portal-profile-toast', role: 'status' }, M.PROFILE_FEATURE_TODO))));
    if (openInnerAsModal && innerView) {
        return (React.createElement(React.Fragment, null,
            hub,
            React.createElement("div", { className: 'portal-profile-submodal-backdrop', onClick: onDetailBack, role: 'presentation' },
                React.createElement("div", { className: `portal-profile-submodal${activeMenuId === 'ai' ? ' is-ai-chat' : ''}`, onClick: event => event.stopPropagation(), role: 'dialog' }, innerView))));
    }
    return hub;
}
